import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import {
  LayoutDashboard,
  MonitorDot,
  School,
  Users,
  Boxes,
  Bell,
  Key,
  CalendarPlus,
  FileSpreadsheet,
  Tag,
  Ticket,
  Image,
  Database,
  Settings,
  Search,
  BookUser,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  ChevronDown,
  X,
  Cog,
  Wrench,
  Network,
  Server,
  Sun,
  Moon,
  Shield,
  ShieldCheck,
  ArrowRightLeft,
  Columns3,
  UserPlus,
} from 'lucide-react';
import ecoleFuteeFavicon from '@/assets/ef_favicon.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

interface NavItem {
  label: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Permission code required to see this item (e.g., 'sidebar.schools') */
  permission?: string;
  /** Child items for collapsible groups */
  children?: NavItem[];
  /** Badges to display next to the label (e.g., 'v1', 'v2') */
  badges?: string[];
  /** Visually highlight this item as a special/upcoming feature */
  highlight?: boolean;
  /** Tag to display (e.g., 'Bientôt') */
  tag?: string;
}

/**
 * Navigation items with permission-based access control
 *
 * Each item can have a permission code that maps to the database Permission table.
 * If no permission is specified, the item is visible to all authenticated users.
 * Items with children are collapsible groups.
 *
 * @see /docs/PERMISSIONS.md for full documentation
 */
const navItems: NavItem[] = [
  // Accessible to everyone
  { label: 'Tableau de bord', href: '/', icon: LayoutDashboard },
  { label: 'Pilotage', href: '/pilotage', icon: MonitorDot, permission: 'sidebar.dashboard_live', badges: ['v1', 'v2'] },

  // Main sections
  { label: 'Écoles', href: '/schools', icon: School, permission: 'sidebar.schools', badges: ['v1', 'v2'] },
  { label: 'Reseaux', href: '/school-networks', icon: Network, permission: 'sidebar.school_networks', badges: ['v2'] },
  { label: 'Marques blanches', href: '/white-labels', icon: Tag, permission: 'sidebar.white_labels', badges: ['v1', 'v2'] },

  // Référentiels group
  {
    label: 'Référentiels',
    icon: Cog,
    children: [
      { label: 'Modules', href: '/modules', icon: Boxes, permission: 'sidebar.modules', badges: ['v1', 'v2'] },
      { label: 'Produits', href: '/products', icon: Ticket, permission: 'sidebar.offers', badges: ['v2'] },
      { label: 'Années fiscales', href: '/school-years', icon: CalendarPlus, permission: 'sidebar.school_years', badges: ['v1'] },
    ],
  },

  // Outils group
  {
    label: 'Outils',
    icon: Wrench,
    children: [
      { label: 'Migrations', href: '/migrations', icon: Settings, permission: 'sidebar.migrations', badges: ['v1'] },
      { label: 'Bases de donnees', href: '/databases', icon: Database, permission: 'sidebar.databases', badges: ['v1'] },
      { label: 'Exports', href: '/exports', icon: FileSpreadsheet, permission: 'sidebar.exports', badges: ['v1'] },
      { label: 'Notifications', href: '/notifications', icon: Bell, permission: 'sidebar.notifications', badges: ['v1'] },
      { label: 'Recherches', href: '/search', icon: Search, permission: 'sidebar.search', badges: ['v1'] },
      { label: 'Photos du login', href: '/login-photos', icon: Image, permission: 'sidebar.login_photos', badges: ['v1', 'v2'] },
    ],
  },

  // Administrateurs (standalone)
  { label: 'Administrateurs', href: '/administrators', icon: ShieldCheck, permission: 'sidebar.administrators', badges: ['v2'] },

  // Administration group
  {
    label: 'Gestion des comptes',
    icon: Shield,
    children: [
      { label: 'Comptes', href: '/accounts', icon: BookUser, permission: 'sidebar.accounts' },
      { label: 'Permissions', href: '/admin-permissions', icon: Key, permission: 'sidebar.admin_permissions' },
    ],
  },

  // Migration V1 → V2
  {
    label: 'Migration V1 → V2',
    icon: ArrowRightLeft,
    permission: 'sidebar.migration_v1_v2',
    highlight: true,
    children: [
      { label: 'Tableau de bord', href: '/migration-v1-v2/dashboard', icon: ArrowRightLeft, permission: 'sidebar.migration_v1_v2' },
      { label: 'Board', href: '/migration-v1-v2/board', icon: Columns3, permission: 'sidebar.migration_v1_v2' },
      { label: 'Écoles', href: '/migration-v1-v2/schools', icon: School, permission: 'sidebar.migration_v1_v2' },
      { label: 'Rattrapage auto', href: '/migration-v1-v2/rattrapage', icon: UserPlus, permission: 'sidebar.migration_v1_v2' },
      { label: 'Configuration', href: '/migration-v1-v2/config', icon: Settings, permission: 'action.migration_v1_v2.config' },
    ],
  },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission, logout } = useAuth();
  const { isCollapsed, isMobileOpen, toggleCollapse, closeMobile } = useSidebar();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage
  useEffect(() => {
    const savedDark = localStorage.getItem('ecole-futee-dark') === 'true';
    setIsDarkMode(savedDark);
    if (savedDark) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDark = !isDarkMode;
    setIsDarkMode(newDark);
    localStorage.setItem('ecole-futee-dark', String(newDark));
    if (newDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Filter items based on permissions (including children)
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return items
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterNavItems(item.children);
          // Only show parent if at least one child is visible
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        if (item.permission && !hasPermission(item.permission)) return null;
        return item;
      })
      .filter((item): item is NavItem => item !== null);
  };

  const filteredNavItems = filterNavItems(navItems);

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  };

  const isGroupExpanded = (label: string) => expandedGroups.includes(label);

  // Check if any child is active (for highlighting parent)
  const isChildActive = (children?: NavItem[]) => {
    if (!children) return false;
    return children.some((child) => child.href && location.pathname.startsWith(child.href));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    closeMobile();
  };

  const initials = user
    ? `${user.firstname?.charAt(0) || ''}${user.lastname?.charAt(0) || ''}`.toUpperCase() || 'U'
    : 'U';

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <nav
        className={cn(
          'fixed lg:relative z-50 flex h-full flex-col bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700 transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Header - Logo */}
        <div className={cn(
          'flex items-center border-b border-gray-200 dark:border-slate-700 h-14',
          isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
        )}>
          <Link to="/" className="flex items-center gap-2" onClick={handleNavClick}>
            <img
              src={ecoleFuteeFavicon}
              alt="École Futée"
              className={cn('w-auto', isCollapsed ? 'h-8' : 'h-8')}
            />
            {!isCollapsed && <span className="text-sm font-bold text-gray-600 dark:text-gray-300">Administration</span>}
          </Link>

          {/* Close button mobile */}
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={closeMobile}
          >
            <X className="h-4 w-4" />
          </Button>

          {/* Collapse button desktop */}
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="hidden lg:flex"
              onClick={toggleCollapse}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {isCollapsed && (
          <div className="hidden lg:flex justify-center py-2 border-b border-gray-200 dark:border-slate-700">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleCollapse}
            >
              <ChevronLeft className="h-4 w-4 rotate-180" />
            </Button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-2">
          <ul className={cn('space-y-1', isCollapsed ? 'px-2' : 'px-3')}>
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = isGroupExpanded(item.label);
              const childActive = isChildActive(item.children);

              // Group with children
              if (hasChildren) {
                return (
                  <li key={item.label}>
                    <button
                      onClick={() => toggleGroup(item.label)}
                      title={isCollapsed ? item.label : undefined}
                      className={cn(
                        'group flex w-full items-center rounded-lg text-sm font-medium transition-all duration-200',
                        isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5',
                        childActive
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-slate-800 dark:hover:text-gray-100'
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 truncate text-left">{item.label}</span>
                          <ChevronRight
                            className={cn(
                              'h-4 w-4 shrink-0 transition-transform duration-200',
                              isExpanded && 'rotate-90'
                            )}
                          />
                        </>
                      )}
                    </button>
                    {/* Children */}
                    {!isCollapsed && isExpanded && (
                      <ul className="mt-1 space-y-1 pl-4">
                        {item.children!.map((child) => {
                          const isActive = child.href ? location.pathname.startsWith(child.href) : false;
                          const ChildIcon = child.icon;
                          return (
                            <li key={child.href}>
                              <Link
                                to={child.href!}
                                onClick={handleNavClick}
                                className={cn(
                                  'group flex items-center rounded-lg text-sm font-medium transition-all duration-200 gap-3 px-3 py-2',
                                  isActive
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                                )}
                              >
                                <ChildIcon className="h-4 w-4 shrink-0" />
                                <span className="truncate">{child.label}</span>
                                {child.badges && child.badges.length > 0 && (
                                  <div className="ml-auto flex gap-1">
                                    {child.badges.map((badge) => (
                                      <span
                                        key={badge}
                                        className={cn(
                                          'px-1.5 py-0.5 text-[10px] font-semibold rounded',
                                          badge === 'v2'
                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                            : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                        )}
                                      >
                                        {badge}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              // Regular item
              // Use startsWith for routes that have sub-pages (detail, edit, create)
              const isActive = item.href === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.href!);

              // Highlighted item (upcoming feature)
              if (item.highlight) {
                return (
                  <li key={item.href} className={cn(!isCollapsed && 'mt-4 pt-4 border-t border-gray-200 dark:border-slate-700')}>
                    <Link
                      to={item.href!}
                      onClick={handleNavClick}
                      title={isCollapsed ? item.label : undefined}
                      className={cn(
                        'group flex items-center rounded-lg text-sm font-medium transition-all duration-200',
                        isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5',
                        isActive
                          ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
                          : 'bg-gradient-to-r from-violet-50 to-indigo-50 text-violet-700 hover:from-violet-100 hover:to-indigo-100 dark:from-violet-900/20 dark:to-indigo-900/20 dark:text-violet-300 dark:hover:from-violet-900/30 dark:hover:to-indigo-900/30 border border-violet-200/60 dark:border-violet-800/40'
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!isCollapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {item.tag && (
                            <span className="ml-auto px-1.5 py-0.5 text-[10px] font-semibold rounded bg-violet-200 text-violet-700 dark:bg-violet-800/40 dark:text-violet-300">
                              {item.tag}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              }

              return (
                <li key={item.href}>
                  <Link
                    to={item.href!}
                    onClick={handleNavClick}
                    title={isCollapsed ? item.label : undefined}
                    className={cn(
                      'group flex items-center rounded-lg text-sm font-medium transition-all duration-200',
                      isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5',
                      isActive
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!isCollapsed && (
                      <>
                        <span className="truncate">{item.label}</span>
                        {item.badges && item.badges.length > 0 && (
                          <div className="ml-auto flex gap-1">
                            {item.badges.map((badge) => (
                              <span
                                key={badge}
                                className={cn(
                                  'px-1.5 py-0.5 text-[10px] font-semibold rounded',
                                  badge === 'v2'
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                    : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                )}
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* User Menu */}
        <div className={cn('border-t border-gray-200 dark:border-slate-700', isCollapsed ? 'p-2' : 'p-3')}>
          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex w-full items-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-slate-800',
                  isCollapsed ? 'justify-center p-2' : 'gap-3 p-2'
                )}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white text-xs font-semibold shrink-0">
                  {initials}
                </div>
                {!isCollapsed && (
                  <>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.fullname}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user?.roleName}
                      </p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="end"
              sideOffset={8}
              className="w-56"
            >
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.fullname}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                Profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleDarkMode}>
                {isDarkMode ? (
                  <>
                    <Sun className="mr-2 h-4 w-4" />
                    Mode clair
                  </>
                ) : (
                  <>
                    <Moon className="mr-2 h-4 w-4" />
                    Mode sombre
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Deconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </>
  );
}
